import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { downloadAuditPdf, signRun } from '../../api/auditReport'
import { auditNext, auditStop } from '../../api/audit'
import { auditSnapshotQuery, type SnapshotChapter } from '../../api/auditSnapshot'
import { ApiError } from '../../api/client'
import { AdmPageHead } from '../../softsee/adm/AdmPageHead'
import { Forbidden } from '../../ui/Forbidden'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'
import { usePageStyles } from '../../ui/usePageStyles'
import { FindingsTable, WrittenPane } from './findings'

/**
 * `settings/Audit::snapshot` - o fotografie datata, inghetata, la
 * /app/settings/audit/snapshot/$id. Macheta: `settings/audit/snapshot.php`.
 *
 * Un audit care scrie peste ultimul lui raspuns nu poate arata ca ceva s-a
 * miscat. Asa ca o rulare produce foaia asta: capitolele sunt ariile, in
 * ordinea planului, fiecare cu analiza asa cum a fost scrisa atunci, peste
 * dovezile de atunci. Nimic de aici nu se rescrie; raportul si solutiile se
 * adauga LANGA analiza, niciodata peste ea. Trei faze, in trei locuri:
 * analiza (ce e), raportul (adunat si asezat), solutiile (singurul loc pentru
 * sfaturi).
 */
const STYLES = ['css/adm.css', 'css/audit.css']

const STATUS_PILL: Record<string, string> = {
  running: 'adm-pill-running',
  done: 'adm-pill-ok',
  failed: 'adm-pill-bad',
  queued: 'adm-pill-warn',
  stopped: 'adm-pill-warn',
}

export function AuditSnapshotPage({ id }: { id: number }) {
  const page = useQuery(auditSnapshotQuery(id))
  const client = useQueryClient()
  const [editing, setEditing] = useState<'' | 'report' | 'solutions'>('')
  const [said, setSaid] = useState('')
  const refresh = () => client.invalidateQueries({ queryKey: auditSnapshotQuery(id).queryKey })
  const sign = useMutation({
    mutationFn: () => signRun(id),
    onSuccess: (r) => {
      if (!r.ok) setSaid(r.error)
      else void refresh()
    },
    onError: (e: unknown) => setSaid(e instanceof ApiError ? e.message : 'It could not be signed.'),
  })
  const next = useMutation({
    mutationFn: (what: 'report' | 'solutions') => auditNext(what, id),
    onSuccess: (r) => {
      if (!r.ok) setSaid(r.error ?? 'That did not start.')
      else void refresh()
    },
    onError: (e: unknown) => setSaid(e instanceof ApiError ? e.message : 'That did not start.'),
  })
  /* Oprirea: se intreaba intai, fiindca nu se poate lua inapoi (audit.js). */
  const stop = useMutation({
    mutationFn: () => auditStop(id),
    onSuccess: (r) => {
      if (!r.ok) setSaid(r.error ?? 'It could not be stopped.')
      else void refresh()
    },
    onError: (e: unknown) => setSaid(e instanceof ApiError ? e.message : 'It could not be stopped.'),
  })
  const pdf = useMutation({
    mutationFn: () => downloadAuditPdf(id),
    onError: (e: unknown) => setSaid(e instanceof ApiError ? e.message : 'The PDF could not be produced.'),
  })
  usePageStyles(STYLES, page.data?.version ?? '')

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error instanceof ApiError && page.error.status === 404) {
    return (
      <Content>
        <div id="content" className="od-audit">
          <p>No such run. <a href="/app/settings/audit">Back to IT Audit</a>.</p>
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />

  const d = page.data
  const { run, type, chapters, html, hand, evidence, probes, findings, seal, signed, mayRun, maySign, review, cost } = d
  const status = run.status
  const answered = chapters.filter((c) => c.status === 'done').length
  const unsigned = run.signed_at === null

  return (
    <Content>
      <div id="content" className="od-audit od-audit-snapshot" data-run={run.ID} data-status={status} data-pending={d.pending > 0 ? 1 : 0}>
        <AdmPageHead
          tone="blue-mauve"
          icon="fa-camera"
          title={`${type ? type.title : 'Audit'} — ${run.taken}`}
          note="A snapshot of the estate as it was when this ran. Nothing on this sheet is rewritten: the report and the proposed solutions are added beside it, never over it."
          stats={[
            { value: `${answered}/${chapters.length}`, label: 'chapters analysed', tone: 'blue-mauve' },
            { value: probes.length ? String(probes.length) : '—', label: 'live probes', tone: 'blue-mauve' },
            { value: evidence.length ? String(evidence.length) : '—', label: 'measured', tone: 'blue-mauve' },
            { value: cost.minutes > 0 ? `${cost.minutes} min` : '—', label: `${cost.tokens.toLocaleString('ro-RO')} tokens`, tone: 'blue-mauve' },
          ]}
        />

        <div className="adm-card od-audit-snap-head">
          <div className="od-audit-run-scope">
            <span className="od-audit-run-label">Scope</span>
            <strong>{run.scope !== '' ? run.scope : '—'}</strong>
            <span className="adm-muted">
              taken {run.taken}
              {run.probe_set !== '' && <> &middot; probe set {run.probe_set}</>}
              {run.brief_set !== '' && <> &middot; briefing {run.brief_set}</>}
            </span>
          </div>
          <div className="od-audit-run-state">
            <span className={`adm-pill ${STATUS_PILL[status] ?? ''}`}>{status === 'done' ? 'analysis complete' : status}</span>
            {status === 'stopped' && run.error !== '' && <span className="adm-muted">{run.error}</span>}
          </div>
          <div className="od-audit-run-act">
            <Link className="btn btn-default" to="/settings/audit/paper/$id" params={{ id: String(run.ID) }} target="_blank" rel="noopener">
              <i className="fas fa-file-alt" aria-hidden="true" /> As a document
            </Link>
            <button type="button" className="btn btn-default" onClick={() => pdf.mutate()} disabled={pdf.isPending}>
              <i className="fas fa-file-pdf" aria-hidden="true" /> {pdf.isPending ? 'Preparing…' : 'Download PDF'}
            </button>
            {(status === 'running' || status === 'queued') && mayRun && (
              <button
                type="button"
                className="btn btn-default od-audit-stop"
                data-run={run.ID}
                disabled={stop.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      'Stop this audit?\n\nThe chapters already answered are kept. The ones still waiting will not be asked - to get them you would start a new run.',
                    )
                  )
                    stop.mutate()
                }}
              >
                <i className="fas fa-stop" aria-hidden="true" /> Stop this run
              </button>
            )}
            {status === 'done' && unsigned && maySign && (
              <button type="button" className="btn btn-default od-audit-sign" data-run={run.ID} onClick={() => sign.mutate()} disabled={sign.isPending}>
                <i className="fas fa-signature" aria-hidden="true" /> Sign it
              </button>
            )}
            <a className="btn btn-default" href={`/app/settings/audit/type/${run.type_id}`}>
              <i className="fas fa-list-ul" aria-hidden="true" /> The plan
            </a>
          </div>
        </div>

        {said !== '' && (
          <div className="adm-card">
            <span className="text-danger">{said}</span>
          </div>
        )}

        {!unsigned ? (
          <div className={`adm-card od-audit-signed${signed === false ? ' is-broken' : ''}`}>
            <i className={`fas fa-${signed === false ? 'triangle-exclamation' : 'signature'}`} aria-hidden="true" />{' '}
            {signed === false ? (
              <>
                <strong>The signature no longer covers this snapshot.</strong>{' '}
                <span className="adm-muted">
                  Something was regenerated after it was signed - the document and the signature no longer describe the same thing.
                </span>
              </>
            ) : (
              <>
                <strong>Signed {run.signed_at}.</strong>{' '}
                <span className="adm-muted">The signature covers the analysis, the report, the proposed solutions and the evidence as they stand.</span>
              </>
            )}
          </div>
        ) : review.length > 0 ? (
          <div className="adm-card od-audit-review">
            <strong>
              <i className="fas fa-user-check" aria-hidden="true" /> The manual asks a lead auditor to look at these before the ratings are
              taken as final
            </strong>
            <ul>
              {review.map((one, i) => (
                <li key={i}>
                  <strong>{one.title}</strong> &mdash; {one.why}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="adm-section adm-section-blue-mauve">
          <h4>
            <i className="fas fa-microscope" aria-hidden="true" /> Analysis
          </h4>
          <span className="adm-section-note">what is, chapter by chapter - state and evidence, no advice</span>
        </div>
        {chapters.map((chapter, i) => (
          <Chapter key={chapter.ID} chapter={chapter} openAtFirst={i === 0 && chapter.html !== ''} />
        ))}

        {findings.length > 0 && (
          <>
            <div className="adm-section adm-section-red-intense">
              <h4>
                <i className="fas fa-exclamation-triangle" aria-hidden="true" /> Findings
              </h4>
              <span className="adm-section-note">worst first - the same rows the report and the roll-up count</span>
            </div>
            <FindingsTable rows={findings} />
          </>
        )}

        <div className="adm-section adm-section-blue-mauve" id="report">
          <h4>
            <i className="fas fa-file-alt" aria-hidden="true" /> Report
          </h4>
          <span className="adm-section-note">
            {html.report !== '' ? <>written {run.report_at} from the analysis above</> : 'the analysis, gathered and settled - still no advice'}
          </span>
          {html.report === '' && status === 'done' && mayRun && (
            <button type="button" className="btn btn-primary btn-sm adm-section-link od-audit-next" onClick={() => next.mutate('report')} disabled={next.isPending}>
              <i className="fas fa-file-signature" aria-hidden="true" /> Generate Report
            </button>
          )}
          {html.report !== '' && mayRun && unsigned && editing !== 'report' && (
            <button type="button" className="btn btn-default btn-sm adm-section-link od-audit-edit" onClick={() => setEditing('report')}>
              <i className="fas fa-pen" aria-hidden="true" /> Edit before it is signed
            </button>
          )}
        </div>
        <div className="adm-card od-audit-report" data-pane="report">
          <WrittenPane
            run={run.ID}
            what="report"
            html={html.report}
            text={run.report}
            hand={hand.report}
            draftKept={run.report_ai}
            open={editing === 'report'}
            onClose={() => setEditing('')}
            onWritten={() => void refresh()}
            empty={
              status !== 'done' ? (
                <p className="adm-muted">The analysis is still running. The report is written from it, once it is whole.</p>
              ) : (
                <p className="adm-muted">Not written yet. Press Generate Report - Adam reads the chapters above and settles them, in the drawer.</p>
              )
            }
            note={
              <>
                What the agent wrote is kept whatever you do here, and the document says the report was edited, by whom and when. The
                chapters above cannot be edited at all: they are the working papers of a dated snapshot, and rewriting one would make this
                run impossible to compare with the next.
              </>
            }
          />
        </div>

        <div className="adm-section adm-section-green-mode" id="solutions">
          <h4>
            <i className="fas fa-lightbulb" aria-hidden="true" /> Proposed solutions
          </h4>
          <span className="adm-section-note">
            {html.solutions !== '' ? <>written {run.solutions_at}</> : 'the only place advice belongs - what to do, what it costs, in what order'}
          </span>
          {html.solutions === '' && run.report.trim() !== '' && mayRun && (
            <button type="button" className="btn btn-primary btn-sm adm-section-link od-audit-next" onClick={() => next.mutate('solutions')} disabled={next.isPending}>
              <i className="fas fa-lightbulb" aria-hidden="true" /> Propose solutions
            </button>
          )}
          {html.solutions !== '' && mayRun && unsigned && editing !== 'solutions' && (
            <button type="button" className="btn btn-default btn-sm adm-section-link od-audit-edit" onClick={() => setEditing('solutions')}>
              <i className="fas fa-pen" aria-hidden="true" /> Edit before it is signed
            </button>
          )}
        </div>
        <div className="adm-card od-audit-report od-audit-solutions" data-pane="solutions">
          <WrittenPane
            run={run.ID}
            what="solutions"
            html={html.solutions}
            text={run.solutions}
            hand={hand.solutions}
            draftKept={run.solutions_ai}
            open={editing === 'solutions'}
            onClose={() => setEditing('')}
            onWritten={() => void refresh()}
            empty={
              run.report.trim() === '' ? (
                <p className="adm-muted">The report comes first - the solutions are written from it, not instead of it.</p>
              ) : (
                <p className="adm-muted">Not asked for yet.</p>
              )
            }
            note={<>What the agent wrote is kept whatever you do here, and the document says the solutions were edited, by whom and when.</>}
          />
        </div>

        {(evidence.length > 0 || probes.length > 0) && <Evidence evidence={evidence} probes={probes} seal={seal} />}
      </div>
    </Content>
  )
}

/**
 * Un capitol: nota asa cum a fost socotita atunci, sirul ei in timp, si
 * raspunsul dinainte alaturi cand exista - asa se vede daca s-a schimbat
 * parcul sau doar intrebarea.
 */
function Chapter({ chapter, openAtFirst }: { chapter: SnapshotChapter; openAtFirst: boolean }) {
  const [open, setOpen] = useState(openAtFirst)
  const [compare, setCompare] = useState(false)
  const panel = `chapter${chapter.ID}`
  const pair = `pair${chapter.ID}`
  const rated = chapter.rated
  const bad = chapter.findings_note.includes('dropped') || chapter.findings_note.includes('no findings block')

  return (
    <article className={`adm-card od-audit-chapter${chapter.status === 'done' ? ' is-done' : ''}`}>
      <button type="button" className="od-audit-chapter-head" aria-expanded={open} aria-controls={panel} onClick={() => setOpen(!open)}>
        <span className="od-audit-step-n">{chapter.status === 'done' ? <i className="fas fa-check" aria-hidden="true" /> : chapter.ord}</span>
        <span className="od-audit-chapter-title">{chapter.title}</span>
        {rated && rated.rating !== null && (
          <>
            <span
              className={`od-audit-rate od-audit-rate-${rated.colour.toLowerCase()}`}
              title={`automated ${rated.automated.toFixed(1)} at ${rated.weights.auto}%, client ${rated.manual} at ${rated.weights.manual}%`}
            >
              {rated.rating}/5 {rated.colour}
            </span>
            {chapter.history.length > 1 && (
              <span className="od-audit-trend" title={chapter.drift}>
                {chapter.history.map((point, i) => (
                  <span key={i} className={`od-audit-tick od-audit-tick-${point.colour.toLowerCase()}`} title={`${point.at}: ${point.rating}/5`}>
                    {point.rating}
                  </span>
                ))}
                <span className="od-audit-drift">{chapter.drift}</span>
              </span>
            )}
            {rated.gap !== undefined && rated.gap !== '' && rated.gap !== 0 && (
              <span className="od-audit-rate-gap" title="the manual says resolve this before rating">
                halves disagree by {String(rated.gap)}
              </span>
            )}
          </>
        )}
        {chapter.status !== 'done' && <span className="adm-pill adm-pill-warn">{chapter.status}</span>}
        {chapter.findings_note.trim() !== '' && <span className={`od-audit-fnote${bad ? ' is-bad' : ''}`}>{chapter.findings_note}</span>}
        <span className="adm-muted">{chapter.finished_at}</span>
        <i className="fas fa-chevron-right od-audit-chevron" aria-hidden="true" />
      </button>
      <div className="od-audit-chapter-body od-audit-report" id={panel} hidden={!open}>
        {chapter.before && (
          <div className="od-audit-vs-bar">
            <button type="button" className="od-audit-vs-toggle" aria-expanded={compare} aria-controls={pair} onClick={() => setCompare(!compare)}>
              <i className="fas fa-columns" aria-hidden="true" /> Compare with the run of {chapter.before.finished_at}
            </button>
          </div>
        )}
        <div className={`od-audit-vs${chapter.before ? '' : ' is-alone'}`}>
          <div className="od-audit-vs-now">
            {chapter.before && <span className="od-audit-vs-when">This run &middot; {chapter.finished_at}</span>}
            {chapter.html !== '' ? <div dangerouslySetInnerHTML={{ __html: chapter.html }} /> : <p className="adm-muted">Not answered in this run.</p>}
          </div>
          {chapter.before && (
            <div className="od-audit-vs-then" id={pair} hidden={!compare}>
              <span className="od-audit-vs-when">Before &middot; {chapter.before.finished_at}</span>
              <div dangerouslySetInnerHTML={{ __html: chapter.before.html }} />
            </div>
          )}
        </div>
      </div>
    </article>
  )
}

function Evidence({
  evidence,
  probes,
  seal,
}: {
  evidence: { area: string; title: string; verdict: string; figure?: string | number | null }[]
  probes: { device_name: string; area_code: string; probe_key: string; command_text: string; status: string }[]
  seal: boolean | null
}) {
  const [open, setOpen] = useState(false)
  const pill = (verdict: string) => (verdict === 'pass' ? 'adm-pill-ok' : verdict === 'fail' ? 'adm-pill-bad' : verdict === 'observed' ? '' : 'adm-pill-warn')

  return (
    <>
      <div className="adm-section adm-section-grey">
        <h4>
          <i className="fas fa-archive" aria-hidden="true" /> Evidence, as it stood
        </h4>
        <span className="adm-section-note">
          frozen with this snapshot - the figures the analysis above was written from
          {seal === true && (
            <>
              {' '}
              &middot;{' '}
              <span className="od-audit-seal-ok">
                <i className="fas fa-lock" aria-hidden="true" /> unchanged since it was taken
              </span>
            </>
          )}
          {seal === false && (
            <>
              {' '}
              &middot;{' '}
              <span className="od-audit-seal-bad">
                <i className="fas fa-lock-open" aria-hidden="true" /> CHANGED since it was taken - this evidence no longer supports the answers above
              </span>
            </>
          )}
        </span>
      </div>
      <div className="adm-card">
        <button type="button" className="od-audit-spec-toggle" aria-expanded={open} aria-controls="snapEvidence" onClick={() => setOpen(!open)}>
          <i className="fas fa-chevron-right" aria-hidden="true" />
          <span>
            {evidence.length} measured from the CMDB, {probes.length} read live from machines
          </span>
        </button>
        <div className="od-audit-spec-body" id="snapEvidence" hidden={!open}>
          {evidence.length > 0 && (
            <table className="table od-audit-table">
              <thead>
                <tr>
                  <th className="od-audit-th-area">Area</th>
                  <th>What</th>
                  <th>Verdict</th>
                  <th>Figure</th>
                </tr>
              </thead>
              <tbody>
                {evidence.map((row, i) => (
                  <tr key={i}>
                    <td className="adm-nowrap">{row.area}</td>
                    <td>{row.title}</td>
                    <td>
                      <span className={`adm-pill ${pill(row.verdict)}`}>{row.verdict}</span>
                    </td>
                    <td>{row.figure === undefined || row.figure === null ? '—' : String(row.figure)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {probes.length > 0 && (
            <table className="table od-audit-table">
              <thead>
                <tr>
                  <th className="od-audit-th-area">Machine</th>
                  <th>Probe</th>
                  <th>Command</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {probes.map((probe, i) => (
                  <tr key={i}>
                    <td className="adm-nowrap">{probe.device_name}</td>
                    <td>
                      {probe.area_code} {probe.probe_key}
                    </td>
                    <td>
                      <code>{probe.command_text.slice(0, 90)}</code>
                    </td>
                    <td>
                      <span className={`adm-pill ${probe.status === 'done' ? 'adm-pill-ok' : 'adm-pill-warn'}`}>{probe.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}
